import React from "react";
import { Box, Flex, Grid, Image, Img, Link, Text } from "@chakra-ui/react";
import FeatureSectionContainer from "@/features/landing/components/common/FeatureSectionContainer";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { Trans } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import CustomIcon from "../../../../public/icons-v1_1";
import { PATHPICK_LANDING_PATH } from "@/features/pathpick/features/landing/constants";

// 시안 색상 — 메인 랜딩은 Chakra 테마 토큰 대신 hex 리터럴을 쓴다.
const BLUE = "#1272EF";
const TEXT_MUTED = "#737373";
const SURFACE = "#FFFFFF";
const SECTION_BACKGROUND = "#FFFFFF";
const CARD_BORDER = "#E4E4E1";
// 흰 패널 위에 얹히는 흰 카드용 — CARD_BORDER보다 한 단계 연하게.
const CARD_BORDER_SOFT = "#EFEFEF";
const CARD_SHADOW = "0px 2px 8px rgba(0, 0, 0, 0.06)";

// i18n 서브키와 1:1로 대응하는 피처 카드 순서.
const FEATURE_KEYS = ["explore", "margin", "channel", "stock"] as const;

export function LandingPagePathPickSection(): JSX.Element {
  const { t, tTitle, tSentence } = useI18nHelper();
  // 로케일 접두사(/ko)를 붙여 보낸다 — 접두사 없는 URL로 가면 루트 로더가 push 리다이렉트를
  // 걸면서 히스토리에 칸이 하나 더 쌓여 뒤로가기가 한 번에 먹지 않는다.
  const { calcNavigateTo } = useRouterHelper();

  return (
    <Flex
      as={"section"}
      aria-labelledby={"landing-pathpick-title"}
      background={SECTION_BACKGROUND}
      paddingX={{
        base: "16px",
        lg: "24px",
      }}
    >
      <FeatureSectionContainer
        maxWidth={"1318px"}
        margin={"0 auto"}
        width={"100%"}
        flexDirection={"column"}
        alignItems={"stretch"}
        gap={{
          base: "32px",
          lg: "48px",
        }}
      >
        <Flex
          flexDirection={"column"}
          gap={{
            base: "12px",
            lg: "24px",
          }}
          alignItems={{
            base: "center",
            md: "flex-start",
          }}
        >
          <Flex alignItems={"center"} gap={"8px"}>
            <Text
              color={BLUE}
              fontSize={{
                base: "16px",
                lg: "18px",
              }}
              fontWeight={700}
              lineHeight={{
                base: "24px",
                lg: "26px",
              }}
              as={"h2"}
              id={"landing-pathpick-title"}
            >
              {tTitle("landing:pathpick.title")}
            </Text>
            <Box
              background={BLUE}
              color={SURFACE}
              borderRadius={"4px"}
              padding={"2px 8px"}
              fontSize={"12px"}
              fontWeight={700}
              lineHeight={"18px"}
            >
              {t("landing:pathpick.badge_new")}
            </Box>
          </Flex>
          <Text
            fontSize={{
              base: "28px",
              lg: "48px",
            }}
            fontWeight={700}
            textAlign={{
              base: "center",
              md: "start",
            }}
            lineHeight={{
              base: "38px",
              lg: "68px",
            }}
          >
            <Trans
              i18nKey={"landing:pathpick.subtitle"}
              components={{
                br: <Box as={"span"} display={"block"} />,
              }}
            />
          </Text>
          <Text
            fontSize={{
              base: "16px",
              lg: "24px",
            }}
            lineHeight={{
              base: "24px",
              lg: "32px",
            }}
            color={TEXT_MUTED}
            textAlign={{
              base: "center",
              md: "start",
            }}
          >
            <Trans
              i18nKey={"landing:pathpick.description"}
              components={{
                br: (
                  <Box
                    as={"span"}
                    display={{
                      base: "none",
                      md: "block",
                    }}
                  />
                ),
              }}
            />
          </Text>
          <Link
            as={RouterLink}
            to={calcNavigateTo(PATHPICK_LANDING_PATH)}
            display={"inline-flex"}
            alignItems={"center"}
            gap={"8px"}
            padding={{
              base: "12px 24px",
              lg: "14px 26px",
            }}
            background={BLUE}
            color={SURFACE}
            borderRadius={"8px"}
            fontSize={{
              base: "16px",
              lg: "20px",
            }}
            fontWeight={700}
            lineHeight={{
              base: "24px",
              lg: "28px",
            }}
            position={"relative"}
            overflow={"hidden"}
            _hover={{
              textDecoration: "none",
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                background: "#2A2A2A",
                opacity: 0.08,
              },
            }}
            _active={{
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                background: "#2A2A2A",
                opacity: 0.2,
              },
            }}
          >
            {tSentence("landing:pathpick_detail")}
            <CustomIcon
              src={"chevron-right"}
              width={24}
              height={24}
              color={SURFACE}
            />
          </Link>
        </Flex>
        <Box
          borderTop={`1px solid ${CARD_BORDER}`}
          paddingTop={{
            base: "24px",
            lg: "32px",
          }}
        >
          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
            }}
            gap={{
              base: "20px",
              lg: "24px",
            }}
          >
            {FEATURE_KEYS.map((key) => (
              <Flex
                key={key}
                flexDirection={"column"}
                gap={"8px"}
                alignItems={{
                  base: "center",
                  md: "flex-start",
                }}
              >
                <Text color={BLUE} fontSize={"16px"} fontWeight={700}>
                  {tTitle(`landing:pathpick.features.${key}.title`)}
                </Text>
                <Text color={TEXT_MUTED} fontSize={"14px"} lineHeight={"20px"}>
                  <Trans
                    i18nKey={`landing:pathpick.features.${key}.description`}
                    components={{
                      // 카드 폭과 무관하게 서술부 앞에서 항상 끊는다.
                      br: <Box as={"span"} display={"block"} />,
                    }}
                  />
                </Text>
              </Flex>
            ))}
          </Grid>
        </Box>
        <PathPickShowcase />
      </FeatureSectionContainer>
    </Flex>
  );
}

/**
 * 시안 하단의 소개용 목업. 실제 데이터가 아니라 화면 예시이므로 금액도 i18n 문자열 그대로 쓴다.
 * 상품 카드는 시안처럼 살짝 기울여(rotate) 겹쳐 배치한다.
 */
function PathPickShowcase(): JSX.Element {
  const { t } = useI18nHelper();

  return (
    <Flex
      background={SURFACE}
      border={`1px solid ${CARD_BORDER}`}
      borderRadius={"24px"}
      padding={{
        base: "24px 16px",
        lg: "40px",
      }}
      flexDirection={{
        base: "column",
        lg: "row",
      }}
      alignItems={"center"}
      justifyContent={"center"}
      gap={{
        base: "24px",
        lg: "32px",
      }}
      overflow={"hidden"}
    >
      <Flex
        flexDirection={"column"}
        alignItems={{
          base: "center",
          lg: "center",
        }}
        gap={"32px"}
        flex={1}
        maxWidth={"420px"}
        width={"100%"}
      >
        <Flex alignItems={"flex-end"} gap={"16px"} transform={"rotate(-2deg)"}>
          <ShowcaseProductCard
            imageSrc={"/images/landing-pathpick-product-left.webp"}
            brand={t("landing:pathpick.showcase.product1_brand")}
            name={t("landing:pathpick.showcase.product1_name")}
            priceLabel={t("landing:pathpick.showcase.kream_price")}
            price={t("landing:pathpick.showcase.product1_price")}
            transform={"translateY(12px)"}
          />
          <ShowcaseProductCard
            imageSrc={"/images/landing-pathpick-product-right.webp"}
            brand={t("landing:pathpick.showcase.product2_brand")}
            name={t("landing:pathpick.showcase.product2_name")}
            priceLabel={t("landing:pathpick.showcase.kream_price")}
            price={t("landing:pathpick.showcase.product2_price")}
          />
        </Flex>
        <Flex
          flexDirection={"column"}
          gap={"12px"}
          padding={"16px"}
          background={SURFACE}
          border={`1px solid ${CARD_BORDER_SOFT}`}
          borderRadius={"12px"}
          boxShadow={CARD_SHADOW}
          width={"100%"}
          maxWidth={"320px"}
          // transform={"rotate(-2deg)"}
        >
          <Flex alignItems={"center"} justifyContent={"space-between"}>
            <Text fontSize={"14px"} fontWeight={700}>
              {t("landing:pathpick.showcase.margin_title")}
            </Text>
            <Box
              border={`1px solid ${CARD_BORDER}`}
              borderRadius={"full"}
              padding={"2px 8px"}
              color={TEXT_MUTED}
              fontSize={"11px"}
              lineHeight={"14px"}
            >
              {t("landing:pathpick.showcase.example")}
            </Box>
          </Flex>
          <Grid
            templateColumns={"repeat(2, 1fr)"}
            gap={"1px"}
            background={CARD_BORDER}
            border={`1px solid ${CARD_BORDER}`}
            borderRadius={"8px"}
            overflow={"hidden"}
          >
            <ShowcaseMetric
              label={t("landing:pathpick.showcase.supply_price")}
              value={t("landing:pathpick.showcase.supply_price_value")}
            />
            <ShowcaseMetric
              label={t("landing:pathpick.showcase.selling_price")}
              value={t("landing:pathpick.showcase.selling_price_value")}
            />
            <ShowcaseMetric
              label={t("landing:pathpick.showcase.profit")}
              value={t("landing:pathpick.showcase.profit_value")}
              valueColor={BLUE}
            />
            <ShowcaseMetric
              label={t("landing:pathpick.showcase.margin_rate")}
              value={t("landing:pathpick.showcase.margin_rate_value")}
              valueColor={BLUE}
            />
          </Grid>
          <Text fontSize={"11px"} lineHeight={"16px"} color={TEXT_MUTED}>
            {t("landing:pathpick.showcase.margin_note")}
          </Text>
        </Flex>
      </Flex>
      <Flex
        aria-hidden={true}
        flexShrink={0}
        alignItems={"center"}
        justifyContent={"center"}
        transform={{
          base: "rotate(90deg)",
          lg: "none",
        }}
      >
        <ShowcaseArrowIcon />
      </Flex>
      <Flex
        flexDirection={"column"}
        gap={"16px"}
        padding={{
          base: "20px",
          lg: "24px",
        }}
        background={SURFACE}
        border={`1px solid ${CARD_BORDER}`}
        borderRadius={"16px"}
        flex={1}
        maxWidth={"440px"}
        width={"100%"}
        marginLeft={{
          base: 0,
          lg: "24px",
        }}
      >
        <Flex flexDirection={"column"} gap={"8px"}>
          <Text fontSize={"18px"} fontWeight={700}>
            {t("landing:pathpick.showcase.channel_title")}
          </Text>
          <Text fontSize={"14px"} lineHeight={"20px"} color={TEXT_MUTED}>
            {t("landing:pathpick.showcase.channel_description")}
          </Text>
        </Flex>
        <Grid templateColumns={"repeat(2, 1fr)"} gap={"12px"}>
          <ShowcaseChannelTile
            iconSrc={"/naver_icon.png"}
            label={t("landing:pathpick.showcase.channel_smart_store")}
          />
          <ShowcaseChannelTile
            iconSrc={"/kream_icon.png"}
            label={t("landing:pathpick.showcase.channel_kream")}
          />
        </Grid>
      </Flex>
    </Flex>
  );
}

function ShowcaseProductCard({
  imageSrc,
  brand,
  name,
  priceLabel,
  price,
  transform,
}: {
  imageSrc: string;
  brand: string;
  name: string;
  priceLabel: string;
  price: string;
  transform?: string;
}): JSX.Element {
  return (
    <Flex
      flexDirection={"column"}
      background={SURFACE}
      border={`1px solid ${CARD_BORDER_SOFT}`}
      borderRadius={"12px"}
      boxShadow={CARD_SHADOW}
      // 이미지 상단 모서리는 이 overflow가 대신 깎는다.
      overflow={"hidden"}
      width={{
        base: "132px",
        lg: "148px",
      }}
      transform={transform}
    >
      <Img
        src={imageSrc}
        alt={""}
        aria-hidden={true}
        loading={"lazy"}
        display={"block"}
        width={"100%"}
        height={{
          base: "110px",
          lg: "124px",
        }}
        flexShrink={0}
        objectFit={"cover"}
        objectPosition={"center"}
      />
      <Flex flexDirection={"column"} gap={"8px"} padding={"12px"}>
        <Text fontSize={"12px"} fontWeight={700}>
          {brand}
        </Text>
        <Text fontSize={"12px"} lineHeight={"16px"} color={TEXT_MUTED}>
          {name}
        </Text>
        <Flex
          flexDirection={"column"}
          gap={"2px"}
          borderTop={`1px solid ${CARD_BORDER}`}
          paddingTop={"8px"}
        >
          <Text fontSize={"11px"} lineHeight={"14px"} color={TEXT_MUTED}>
            {priceLabel}
          </Text>
          <Text fontSize={"14px"} fontWeight={700} color={"#2A2A2A"}>
            {price}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
}

function ShowcaseMetric({
  label,
  value,
  valueColor,
}: {
  label: string;
  value: string;
  valueColor?: string;
}): JSX.Element {
  return (
    <Flex
      flexDirection={"column"}
      gap={"2px"}
      padding={"10px 12px"}
      background={SURFACE}
    >
      <Text fontSize={"12px"} lineHeight={"16px"} color={TEXT_MUTED}>
        {label}
      </Text>
      <Text fontSize={"14px"} fontWeight={700} color={valueColor}>
        {value}
      </Text>
    </Flex>
  );
}

function ShowcaseChannelTile({
  iconSrc,
  label,
}: {
  iconSrc: string;
  label: string;
}): JSX.Element {
  return (
    <Flex
      flexDirection={"column"}
      alignItems={"center"}
      justifyContent={"center"}
      gap={"8px"}
      padding={"16px"}
      border={`1px solid ${CARD_BORDER}`}
      borderRadius={"12px"}
    >
      <Image
        src={iconSrc}
        fallbackSrc={"/icons-v1_1/null.png"}
        alt={""}
        aria-hidden={true}
        loading={"lazy"}
        width={"36px"}
        height={"36px"}
      />
      <Text fontSize={"14px"} fontWeight={700}>
        {label}
      </Text>
    </Flex>
  );
}

// 긴 화살표 에셋이 icons-v1_1에 없어서 장식용으로만 직접 그린다.
function ShowcaseArrowIcon(): JSX.Element {
  return (
    <Box
      as={"svg"}
      width={"20px"}
      height={"20px"}
      viewBox={"0 0 20 20"}
      fill={"none"}
      aria-hidden={true}
    >
      <path
        d={"M3 10H16M16 10L11.5 5.5M16 10L11.5 14.5"}
        stroke={TEXT_MUTED}
        strokeWidth={"1.6"}
        strokeLinecap={"round"}
        strokeLinejoin={"round"}
      />
    </Box>
  );
}
